import { useRef, useState } from "react";
import { useAdmin } from "../admin";
import { fileTag, useSignedUrl } from "../lib/files";
import { errorText, supabase } from "../supabase";

// One audio clip of a scenario. Clips are .m4a files in the scenario-audio bucket under
// the company id, at the `audio/...` path the scenario file names, which is where phones
// download them from. Each upload gets a new name, so a phone never keeps an old clip.

const MAX_BYTES = 2 * 1024 * 1024;

export default function AudioField(props: {
  /** Where the clip goes: audio/<scenario id>/<language>/<name>-<tag>.m4a */
  folder: string | null;
  name: string;
  value: string | undefined;
  onChange: (path: string | undefined) => void;
  disabled?: boolean;
}) {
  const { org } = useAdmin();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState("");
  const url = useSignedUrl("scenario-audio", props.value ? `${org.id}/${props.value}` : null);

  async function upload(file: File) {
    if (!props.folder) return;
    if (!/\.m4a$/i.test(file.name)) {
      setState("Choose an .m4a file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setState("That clip is over 2 MB. Please use a shorter or more compressed clip.");
      return;
    }
    setState("Uploading…");
    const path = `${props.folder}/${props.name}-${fileTag()}.m4a`;
    const { error } = await supabase.storage
      .from("scenario-audio")
      .upload(`${org.id}/${path}`, file, { contentType: "audio/mp4", upsert: false });
    if (error) {
      setState(errorText(error));
      return;
    }
    setState("");
    props.onChange(path);
  }

  return (
    <div className="audio">
      {props.value ? (
        url ? <audio controls preload="none" src={url} /> : <span className="small muted">Clip added</span>
      ) : (
        <span className="small muted">No audio</span>
      )}
      <input
        ref={input}
        type="file"
        accept=".m4a,audio/mp4,audio/x-m4a"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) upload(file);
        }}
      />
      <button
        type="button"
        className="btn quiet small"
        disabled={props.disabled || !props.folder}
        title={props.folder ? undefined : "Give the scenario an id first"}
        onClick={() => input.current?.click()}
      >
        {props.value ? "Replace audio" : "Upload audio"}
      </button>
      {props.value && !props.disabled && (
        <button type="button" className="link-btn small" onClick={() => props.onChange(undefined)}>
          Remove
        </button>
      )}
      {state && <span className="small muted">{state}</span>}
    </div>
  );
}
